import React from "react";
import { Box, Grid, Typography, Stack, Link, IconButton } from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneIcon from "@mui/icons-material/Phone";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebookF } from "@fortawesome/free-brands-svg-icons";
import { fadeUp, viewportOnce } from "../../styles/motion";

const LAO_TELECOM_BLUE_LIGHT = "#5B5FBE";

interface FooterColumnProps {
  title: string;
  items: Item[];
}

interface Item {
  label: string;
  href: string;
}

const FooterColumn: React.FC<FooterColumnProps> = ({ title, items }) => {
  return (
    <Stack spacing={1.25}>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ color: "rgba(255,255,255,0.95)", letterSpacing: "0.02em" }}
      >
        {title}
      </Typography>

      {items.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          underline="none"
          sx={{
            fontSize: 13,
            color: "rgba(255,255,255,0.7)",
            transition: "color 0.2s",
            "&:hover": { color: "primary.light" },
          }}
        >
          {item.label}
        </Link>
      ))}
    </Stack>
  );
};

const contactIconButtonSx = {
  bgcolor: "primary.main",
  color: "primary.contrastText",
  width: 32,
  height: 32,
  "&:hover": { bgcolor: "primary.light" },
} as const;

const Footer: React.FC = () => {
  return (
    <Box
      component={motion.footer}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      sx={{
        bgcolor: "#1A1A1A",
        color: "rgba(255,255,255,0.9)",
        mt: 8,
        px: { xs: 3, md: 10 },
        py: { xs: 6, md: 8 },
      }}
    >
      <Grid container spacing={5}>
        {/* LEFT: Logo + Description */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <img
                src="laoTelecomLogo.png"
                alt="Lao Telecom logo"
                style={{ width: 50, height: "auto" }}
              />
              <Stack sx={{ ml: 1 }}>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{ color: "#FFFFFF", lineHeight: 1.1 }}
                >
                  Tourist eSim
                </Typography>
                <Typography
                  fontWeight={500}
                  fontSize="0.7rem"
                  sx={{ color: LAO_TELECOM_BLUE_LIGHT, mt: "2px" }}
                >
                  By Lao Telecom
                </Typography>
              </Stack>
            </Box>

            <Typography
              fontSize={13}
              sx={{ color: "rgba(255,255,255,0.7)", maxWidth: 340 }}
            >
              Lao Telecom eSIM service for worldwide travel. Easy activation,
              fast internet, and reliable connectivity wherever you go.
            </Typography>

            <Box
              component="img"
              src="footer/payment.svg"
              alt="Accepted payment methods"
            />
          </Stack>
        </Grid>

        {/* MIDDLE COLUMNS */}
        <Grid item xs={6} md={2}>
          <FooterColumn
            title="About Us"
            items={[
              { label: "Company Info", href: "/company-profile" },
              { label: "FAQs", href: "/faq" },
              { label: "Privacy Policy", href: "/privacy-policy" },
            ]}
          />
        </Grid>

        <Grid item xs={6} md={2}>
          <FooterColumn
            title="Getting Started"
            items={[{ label: "How to Buy", href: "/how-to-buy" }]}
          />
        </Grid>

        <Grid item xs={6} md={2}>
          <FooterColumn
            title="Destinations"
            items={[
              { label: "Laos eSIM", href: "/" },
              { label: "ASEAN eSIM", href: "/" },
            ]}
          />
        </Grid>

        {/* RIGHT: Contact */}
        <Grid item xs={12} md={2}>
          <Stack spacing={1.5}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: "rgba(255,255,255,0.95)", letterSpacing: "0.02em" }}
            >
              Contact Us
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                href="mailto:shop@laotel.com"
                aria-label="Email"
                sx={contactIconButtonSx}
              >
                <EmailRoundedIcon fontSize="small" />
              </IconButton>
              <Typography
                fontSize={13}
                component="a"
                href="mailto:shop@laotel.com"
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  textDecoration: "none",
                  "&:hover": { color: "primary.light" },
                }}
              >
                shop@laotel.com
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                href="https://www.facebook.com/Laotel/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                sx={contactIconButtonSx}
              >
                <FontAwesomeIcon icon={faFacebookF} />
              </IconButton>
              <Typography
                component={NavLink}
                to="https://www.facebook.com/Laotel/"
                fontSize={13}
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  textDecoration: "none",
                  "&:hover": { color: "primary.light" },
                }}
              >
                Lao Telecom
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                aria-label="Phone"
                sx={contactIconButtonSx}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
              <Typography
                fontSize={13}
                sx={{ color: "rgba(255,255,255,0.85)" }}
              >
                +856 20 52607789
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      {/* Bottom bar */}
      <Box
        sx={{
          mt: 6,
          pt: 3,
          borderTop: "1px solid rgba(255,255,255,0.1)",
          textAlign: "center",
        }}
      >
        <Typography fontSize={12} sx={{ color: "rgba(255,255,255,0.5)" }}>
          © {new Date().getFullYear()} Lao Telecom. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
